import { TOOL_GROUP_THRESHOLD } from '../../constants';
import { createAssistantMetaBar } from '../message-meta-bar';
import { appendMessageAttachments } from './attachments';
import { copyText } from './clipboard';
import { createTextSegmentElement } from './message-element';
import { createRuntimeActivityElement } from '../runtime-activity';
import { createToolCallElement, createToolGroupElement } from './tool-elements';
import { nextLocalId } from './context';
import type { AssistantSegment, ChatMessage, DisplaySegment, RuntimeActivitySegment, TextSegment, ToolCallSegment } from './types';

function isToolCallSegment(segment: AssistantSegment | DisplaySegment): segment is ToolCallSegment & { type: 'tool_call' } {
  return segment.type === 'tool_call';
}

function asDone(done: boolean | undefined): boolean {
  return done !== false;
}

function isTextSegment(segment: AssistantSegment | DisplaySegment): segment is TextSegment {
  return segment.type === 'text';
}

function isRuntimeActivitySegment(segment: AssistantSegment | DisplaySegment): segment is RuntimeActivitySegment {
  return segment.type === 'runtime_activity';
}

function createProcessSegmentElement(text: string): HTMLDivElement {
  return createTextSegmentElement(text, { phase: 'process' });
}

function annotateMessageSegmentPhases(segments: DisplaySegment[]): DisplaySegment[] {
  const list = Array.isArray(segments) ? segments.map((segment) => ({ ...segment })) : [];
  let finalTextIndex = -1;
  for (let index = list.length - 1; index >= 0; index -= 1) {
    const segment = list[index];
    if (isTextSegment(segment) && String(segment.text || '').trim()) {
      finalTextIndex = index;
      break;
    }
  }
  const conclusionTextIndex = finalTextIndex !== -1
    && !list.slice(finalTextIndex + 1).some((segment) => segment && segment.type !== 'text')
    ? finalTextIndex
    : -1;
  return list.map((segment, index) => {
    if (!isTextSegment(segment)) return segment;
    if (segment.phase === 'process' || segment.phase === 'final') return segment;
    return {
      ...segment,
      phase: conclusionTextIndex !== -1 && index === conclusionTextIndex ? 'final' : 'process',
    };
  });
}

function collapseToolSegmentsForDisplay(segments: DisplaySegment[]): DisplaySegment[] {
  const source = Array.isArray(segments) ? segments.filter(Boolean) : [];
  const collapsed: DisplaySegment[] = [];
  let toolRun: Array<ToolCallSegment & { type: 'tool_call' }> = [];

  function flushToolRun(): void {
    if (toolRun.length === 0) return;
    if (toolRun.length >= TOOL_GROUP_THRESHOLD) {
      collapsed.push({
        type: 'tool_group',
        items: toolRun.map((tool) => ({ ...tool, done: asDone(tool.done) })),
      });
    } else {
      collapsed.push(...toolRun.map((tool) => ({ ...tool, done: asDone(tool.done) })));
    }
    toolRun = [];
  }

  source.forEach((segment) => {
    if (isToolCallSegment(segment)) {
      toolRun.push(segment);
      return;
    }
    flushToolRun();
    collapsed.push(segment);
  });

  flushToolRun();
  return collapsed;
}

export function normalizeSegmentsForDisplay(segments: AssistantSegment[]): DisplaySegment[] {
  const normalized = (Array.isArray(segments) ? segments : [])
    .filter(Boolean)
    .map((segment): DisplaySegment => {
      if (segment.type === 'tool_call') {
        return {
          ...segment,
          type: 'tool_call',
          done: asDone(segment.done),
        };
      }
      if (isRuntimeActivitySegment(segment)) {
        return {
          ...segment,
          type: 'runtime_activity',
        };
      }
      const text = isTextSegment(segment) && typeof segment.text === 'string' ? segment.text : '';
      const phase = isTextSegment(segment) && segment.phase === 'process' ? 'process' : isTextSegment(segment) && segment.phase === 'final' ? 'final' : undefined;
      return {
        type: 'text',
        text,
        phase,
      };
    })
    .filter((segment): segment is DisplaySegment => isToolCallSegment(segment) || (isTextSegment(segment) && !!segment.text));
  return annotateMessageSegmentPhases(collapseToolSegmentsForDisplay(normalized));
}

function normalizeMessageSegments(message: ChatMessage): DisplaySegment[] {
  if (Array.isArray(message?.segments) && message.segments.length > 0) {
    return normalizeSegmentsForDisplay(message.segments);
  }

  const fallback: AssistantSegment[] = [];
  if (typeof message?.content === 'string' && message.content) {
    fallback.push({ type: 'text', text: message.content });
  }
  if (Array.isArray(message?.toolCalls)) {
    message.toolCalls.forEach((tool) => {
      if (!tool) return;
      fallback.push({ type: 'tool_call', ...tool, done: asDone(tool.done) });
    });
  }
  return normalizeSegmentsForDisplay(fallback);
}

export function buildMessageSegmentElement(segment: DisplaySegment): Element | null {
  if (!segment) return null;
  if (segment.type === 'tool_group') {
    return createToolGroupElement(segment.items || []);
  }
  if (segment.type === 'tool_call') {
    const details = createToolCallElement(segment.id || nextLocalId('saved'), segment, asDone(segment.done));
    details.classList.add('msg-segment');
    return details;
  }
  if (segment.type === 'runtime_activity') {
    return createRuntimeActivityElement(segment);
  }
  const text = typeof segment.text === 'string' ? segment.text : '';
  if (!text) return null;
  if (segment.phase === 'process') return createProcessSegmentElement(text);
  return createTextSegmentElement(text, { phase: segment.phase || 'final' });
}

export function renderAssistantSegments(bubble: Element | null, message: ChatMessage): void {
  if (!bubble) return;
  bubble.innerHTML = '';
  normalizeMessageSegments(message).forEach((segment) => {
    const segmentEl = buildMessageSegmentElement(segment);
    if (segmentEl) bubble.appendChild(segmentEl);
  });
  appendMessageAttachments(bubble, message?.attachments || []);
  bubble.appendChild(createAssistantMetaBar({ message, copyText }));
}
